<template>
  <div class="venue-management-wrapper">
    <div class="content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <div class="card">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <div class="mr-3 mb-2">
                    <h4 class="card-title">Venue Management</h4>
                    <p class="card-category">Manage venue configurations and mappings</p>
                  </div>
                  <div class="d-flex align-items-center mb-2">
                    <button @click="showAddForm" class="btn btn-info btn-fill shadow-sm d-flex align-items-center py-2 px-3" style="border-radius: 8px; font-size: 16px;">
                      <i class="fas fa-plus mr-2"></i> Add Venue
                    </button>
                    <button @click="refreshVenues" class="btn btn-secondary shadow-sm ml-3 d-flex align-items-center justify-content-center" :disabled="isLoading" style="width: 42px; height: 42px; padding: 0; border-radius: 50%;">
                      <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="card-body">

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>Search by Name:</label>
          <input 
            v-model="filters.name" 
            type="text" 
            placeholder="Enter venue name..."
            @input="applyFilters"
            class="form-control"
          >
        </div>
        <div class="filter-group">
          <label>VividSeats ID:</label>
          <input 
            v-model="filters.vivid_venue_id" 
            type="text" 
            placeholder="Vivid ID..."
            @input="applyFilters"
            class="form-control"
          >
        </div>
        <div class="filter-group">
          <label>ParkWhiz ID:</label>
          <input 
            v-model="filters.pw_venue_id" 
            type="text" 
            placeholder="PW ID..."
            @input="applyFilters"
            class="form-control"
          >
        </div>
        <div class="filter-group">
          <label>StubHub ID:</label>
          <input 
            v-model="filters.sh_venue_id" 
            type="text" 
            placeholder="SH ID..."
            @input="applyFilters"
            class="form-control"
          >
        </div>
        <div class="filter-group">
          <label>MLB Only:</label>
          <select v-model="filters.is_mlb" @change="applyFilters" class="form-control">
            <option value="">All</option>
            <option value="true">MLB Only</option>
            <option value="false">Non-MLB</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="success-message">
      {{ successMessage }}
    </div>

    <!-- Venues Table -->
    <div class="table-section">
      <div class="table-responsive">
        <table class="venues-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>SJ Name</th>
              <th>StubHub ID</th>
              <th>ParkWhiz ID</th>
              <th>VividSeats ID</th>
              <th>MLB</th>
              <th>Mapping Certified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="venue in paginatedVenues" :key="venue.id" @click="editVenue(venue)" style="cursor: pointer">
              <td>{{ venue.id }}</td>
              <td>{{ venue.name }}</td>
              <td>{{ venue.sh_venue_id || '-' }}</td>
              <td>{{ venue.pw_venue_id || '-' }}</td>
              <td>{{ venue.vivid_venue_id || '-' }}</td>
              <td>
                <span :class="venue.is_mlb ? 'status-yes' : 'status-no'">
                  {{ venue.is_mlb ? 'Yes' : 'No' }}
                </span>
              </td>
              <td>
                <span :class="venue.mapping_certified ? 'status-yes' : 'status-no'">
                  {{ venue.mapping_certified ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="actions-cell">
                <button @click.stop="editVenue(venue)" class="btn btn-primary shadow-sm action-btn" title="Edit">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click.stop="confirmDelete(venue)" class="btn btn-danger shadow-sm action-btn" title="Delete">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="pagination-section">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredVenues.length }} entries
        </div>
        <div class="pagination-controls">
          <button 
            @click="previousPage" 
            :disabled="currentPage === 1"
            class="btn btn-secondary"
          >
            Previous
          </button>
          <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages"
            class="btn btn-secondary"
          >
            Next
          </button>
        </div>
      </div>
    </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Venue Form Modal -->
    <b-modal
      v-model="showModal"
      hide-header
      hide-footer
      size="xl"
      centered
      body-class="p-0"
    >
      <div v-if="showModal" class="venue-modal-content">
          <!-- Header -->
          <div class="position-relative bg-white p-4 pb-0 border-bottom">
              <div class="d-flex justify-content-between align-items-start">
                  <div>
                      <h6 class="text-uppercase text-muted font-weight-bold mb-2 small">{{ isEditing ? 'Edit Venue' : 'Create New Venue' }}</h6>
                      <h3 class="font-weight-bold mb-3 text-dark">{{ formData.name || 'New Venue Configuration' }}</h3>
                  </div>
                  <button type="button" class="btn btn-link text-muted p-0" style="font-size: 1.5rem;" @click="closeModal">
                      <i class="fas fa-times"></i>
                  </button>
              </div>
          </div>

          <!-- Body -->
          <div class="p-4 bg-light">
              <form @submit.prevent="submitForm">
                  <div class="card shadow-sm border-0 mb-0 overflow-hidden">
                      <div class="card-body p-0">
                          <b-tabs pill card nav-wrapper-class="bg-white border-bottom p-3" content-class="p-4 bg-white" active-nav-item-class="font-weight-bold">
                              
                              <!-- Basic Information Tab -->
                              <b-tab title="Basic Info" active>
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-4">
                                          <label class="text-muted small text-uppercase font-weight-bold">System Venue Name <span class="text-danger">*</span></label>
                                          <input v-model="formData.name" type="text" class="form-control form-control-lg bg-light border-light" required placeholder="e.g. Dodger Stadium">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-4">
                                          <label class="text-muted small text-uppercase font-weight-bold">Time Zone</label>
                                          <select v-model="formData.timezone" class="form-control form-control-lg bg-light border-light" style="height: 48px;">
                                              <option value="">Select Time Zone...</option>
                                              <option v-for="tz in usTimezones" :key="tz" :value="tz">{{ tz }}</option>
                                          </select>
                                      </div>
                                  </div>
                                  
                                  <h6 class="text-muted text-uppercase small font-weight-bold mb-3 mt-2">Venue Configuration</h6>
                                  <div class="row">
                                      <div class="col-md-6 mb-3">
                                          <div class="p-3 rounded border d-flex align-items-center justify-content-between cursor-pointer transition-all" 
                                               :class="formData.is_mlb ? 'bg-soft-primary border-primary' : 'bg-light'"
                                               @click="formData.is_mlb = !formData.is_mlb">
                                              <div>
                                                  <h6 class="mb-1 font-weight-bold text-dark">MLB Venue</h6>
                                                  <small class="text-muted">Is this a Major League Baseball stadium?</small>
                                              </div>
                                              <div class="custom-control custom-switch">
                                                  <input type="checkbox" class="custom-control-input" v-model="formData.is_mlb">
                                                  <label class="custom-control-label"></label>
                                              </div>
                                          </div>
                                      </div>
                                      <div class="col-md-6 mb-3">
                                          <div class="p-3 rounded border d-flex align-items-center justify-content-between cursor-pointer transition-all"
                                               :class="formData.mapping_certified ? 'bg-soft-success border-success' : 'bg-light'"
                                               @click="formData.mapping_certified = !formData.mapping_certified">
                                              <div>
                                                  <h6 class="mb-1 font-weight-bold text-dark">Mapping Certified</h6>
                                                  <small class="text-muted">Has the lot mapping been verified?</small>
                                              </div>
                                              <div class="custom-control custom-switch">
                                                  <input type="checkbox" class="custom-control-input" v-model="formData.mapping_certified">
                                                  <label class="custom-control-label"></label>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              </b-tab>

                              <!-- StubHub Tab -->
                              <b-tab title="StubHub">
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Parent Venue ID</label>
                                          <input v-model="formData.sh_venue_id" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Venue Name</label>
                                          <input v-model="formData.sh_venue_name" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                                  <hr class="my-3">
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Parking Venue ID</label>
                                          <input v-model="formData.sh_parking_venue_id" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Parking Venue Name</label>
                                          <input v-model="formData.sh_parking_venue_name" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                              </b-tab>

                              <!-- ParkWhiz Tab -->
                              <b-tab title="ParkWhiz">
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Venue ID</label>
                                          <input v-model="formData.pw_venue_id" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Venue Name</label>
                                          <input v-model="formData.pw_venue_name" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                                  <div class="row mt-2">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Latitude</label>
                                          <input v-model="formData.pw_latitude" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Longitude</label>
                                          <input v-model="formData.pw_longitude" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                              </b-tab>

                              <!-- VividSeats Tab -->
                              <b-tab title="VividSeats">
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Parking Venue ID</label>
                                          <input v-model="formData.vivid_venue_id" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Parking Venue Name</label>
                                          <input v-model="formData.vivid_venue_name" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                              </b-tab>

                              <!-- JustPark Tab -->
                              <b-tab title="JustPark">
                                  <div class="row">
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Venue Identifier</label>
                                          <input v-model="formData.jp_venue_identifier" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                      <div class="col-md-6 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">Venue Name</label>
                                          <input v-model="formData.jp_venue_name" type="text" class="form-control form-control-lg bg-light border-light">
                                      </div>
                                  </div>
                                  <div class="row mt-2">
                                      <div class="col-md-12 form-group-modern mb-3">
                                          <label class="text-muted small text-uppercase font-weight-bold">URL Slug</label>
                                          <input v-model="formData.jp_url_slug" type="text" class="form-control form-control-lg bg-light border-light" placeholder="e.g. dodger-stadium">
                                      </div>
                                  </div>
                              </b-tab>

                              <!-- Others Tab -->
                              <b-tab title="Others">
                                  <div class="bg-light p-3 rounded mb-4">
                                      <h6 class="font-weight-bold mb-3 text-primary">ParkMobile</h6>
                                      <div class="row">
                                          <div class="col-md-6 form-group-modern mb-2">
                                              <label class="text-muted small text-uppercase font-weight-bold">Venue ID</label>
                                              <input v-model="formData.pm_venue_id" type="text" class="form-control form-control-lg bg-white border">
                                          </div>
                                          <div class="col-md-6 form-group-modern mb-2">
                                              <label class="text-muted small text-uppercase font-weight-bold">Venue Name</label>
                                              <input v-model="formData.pm_venue_name" type="text" class="form-control form-control-lg bg-white border">
                                          </div>
                                      </div>
                                  </div>
                                  
                                  <div class="row">
                                      <div class="col-md-6">
                                          <div class="bg-light p-3 rounded h-100">
                                              <h6 class="font-weight-bold mb-3 text-primary">SpotHero</h6>
                                              <div class="form-group-modern">
                                                  <label class="text-muted small text-uppercase font-weight-bold">Venue ID</label>
                                                  <input v-model="formData.spothero_venue_id" type="text" class="form-control form-control-lg bg-white border">
                                              </div>
                                          </div>
                                      </div>
                                      <div class="col-md-6">
                                          <div class="bg-light p-3 rounded h-100">
                                              <h6 class="font-weight-bold mb-3 text-primary">SeatGeek</h6>
                                              <div class="form-group-modern">
                                                  <label class="text-muted small text-uppercase font-weight-bold">Venue ID</label>
                                                  <input v-model="formData.sg_venue_id" type="text" class="form-control form-control-lg bg-white border">
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              </b-tab>
                          </b-tabs>
                      </div>
                  </div>

                  <!-- Footer -->
                  <div class="d-flex justify-content-end align-items-center mt-4">
                      <button type="button" @click="closeModal" class="btn btn-link text-muted mr-3 btn-lg">Cancel</button>
                      <button type="submit" class="btn btn-info btn-fill btn-lg px-5 shadow-sm" :disabled="isSubmitting">
                          <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          {{ isEditing ? 'Update Venue' : 'Create Venue' }}
                      </button>
                  </div>
              </form>
          </div>
      </div>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      header-bg-variant="danger"
      header-text-variant="white"
      hide-footer
      size="sm"
      centered
    >
      <div class="text-center p-3">
        <i class="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
        <p>Are you sure you want to delete the venue <strong>"{{ venueToDelete ? venueToDelete.name : '' }}"</strong>?</p>
        <p class="text-muted small">This action cannot be undone.</p>
      </div>
      <div class="d-flex justify-content-center mt-3">
         <b-button variant="secondary" class="mr-2" @click="closeDeleteModal">Cancel</b-button>
         <b-button variant="danger" @click="deleteVenue" :disabled="isDeleting">
            <span v-if="isDeleting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isDeleting ? 'Deleting...' : 'Delete Venue' }}
         </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'VenueManagement',
  data() {
    return {
      venues: [],
      filteredVenues: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      
      // Pagination
      currentPage: 1,
      perPage: 25,
      
      // Filters
      filters: {
        name: '',
        vivid_venue_id: '',
        pw_venue_id: '',
        sh_venue_id: '',
        is_mlb: ''
      },
      
      // Modal states
      showModal: false,
      showDeleteModal: false,
      isEditing: false,
      isSubmitting: false,
      isDeleting: false,
      
      // Form data
      formData: {
        name: '',
        sh_venue_id: null,
        sh_venue_name: '',
        pw_venue_id: null,
        pw_venue_name: '',
        sh_parking_venue_id: null,
        sh_parking_venue_name: '',
        pm_venue_name: '',
        pm_venue_id: null,
        vivid_venue_id: null,
        vivid_venue_name: '',
        is_mlb: false,
        spothero_venue_id: null,
        sg_venue_id: null,
        timezone: '',
        pw_latitude: null,
        pw_longitude: null,
        mapping_certified: false,
        jp_venue_identifier: null,
        jp_venue_name: '',
        jp_url_slug: ''
      },
      
      venueToDelete: null,
      
      // US Timezones
      usTimezones: [
        'America/New_York',
        'America/Chicago', 
        'America/Denver',
        'America/Phoenix',
        'America/Los_Angeles',
        'America/Anchorage',
        'America/Adak',
        'Pacific/Honolulu',
        'America/Detroit',
        'America/Indiana/Indianapolis',
        'America/Indiana/Knox',
        'America/Indiana/Marengo',
        'America/Indiana/Petersburg',
        'America/Indiana/Tell_City',
        'America/Indiana/Vevay',
        'America/Indiana/Vincennes',
        'America/Indiana/Winamac',
        'America/Kentucky/Louisville',
        'America/Kentucky/Monticello',
        'America/North_Dakota/Beulah',
        'America/North_Dakota/Center',
        'America/North_Dakota/New_Salem',
        'America/Boise',
        'America/Juneau',
        'America/Metlakatla',
        'America/Nome',
        'America/Sitka',
        'America/Yakutat'
      ]
    };
  },
  
  computed: {
    totalPages() {
      return Math.ceil(this.filteredVenues.length / this.perPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.filteredVenues.length);
    },
    paginatedVenues() {
      return this.filteredVenues.slice(this.startIndex, this.endIndex);
    }
  },
  
  mounted() {
    this.fetchVenues();
  },
  
  methods: {
    async fetchVenues() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const queryParams = new URLSearchParams();
        
        // Add filters to query if they have values
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== '' && this.filters[key] !== null) {
            queryParams.append(key, this.filters[key]);
          }
        });
        
        const url = `https://api.spotjockeys.com/api/venue-mgt${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        this.venues = data;
        this.applyFilters();
        
      } catch (error) {
        console.error('Error fetching venues:', error);
        this.errorMessage = 'Error fetching venues: ' + error.message;
      } finally {
        this.isLoading = false;
      }
    },
    
    applyFilters() {
      // Apply local filtering on top of API results
      this.filteredVenues = this.venues.filter(venue => {
        return Object.keys(this.filters).every(key => {
          const filterValue = this.filters[key];
          if (!filterValue) return true;
          
          const venueValue = venue[key];
          
          if (key === 'name') {
            return venueValue && venueValue.toLowerCase().includes(filterValue.toLowerCase());
          } else if (key === 'is_mlb') {
            return venueValue.toString() === filterValue;
          } else {
            return venueValue && venueValue.toString() === filterValue.toString();
          }
        });
      });
      
      this.currentPage = 1;
    },
    
    refreshVenues() {
      this.fetchVenues();
    },
    
    showAddForm() {
      this.isEditing = false;
      this.resetForm();
      this.showModal = true;
    },
    
    editVenue(venue) {
      this.isEditing = true;
      this.formData = { ...venue };
      this.showModal = true;
    },
    
    confirmDelete(venue) {
      this.venueToDelete = venue;
      this.showDeleteModal = true;
    },
    
    async deleteVenue() {
      if (!this.venueToDelete) return;
      
      this.isDeleting = true;
      this.errorMessage = '';
      
      try {
        const response = await fetch(`https://api.spotjockeys.com/api/venue-mgt/${this.venueToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        this.successMessage = result.message || 'Venue deleted successfully';
        this.closeDeleteModal();
        this.fetchVenues();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
        
      } catch (error) {
        console.error('Error deleting venue:', error);
        this.errorMessage = 'Error deleting venue: ' + error.message;
      } finally {
        this.isDeleting = false;
      }
    },
    
    async submitForm() {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        // Basic validation
        if (!this.formData.name || this.formData.name.trim() === '') {
          this.errorMessage = 'SJ Venue Name is required';
          return;
        }
        
        // Prepare data for submission
        const submitData = { ...this.formData };
        
        // Convert empty strings to null for numeric fields and string fields
        const numericFields = ['sh_venue_id', 'pw_venue_id', 'sh_parking_venue_id', 'pm_venue_id', 'vivid_venue_id', 'spothero_venue_id', 'sg_venue_id', 'pw_latitude', 'pw_longitude'];
        numericFields.forEach(field => {
          if (submitData[field] === '' || submitData[field] === undefined) {
            submitData[field] = null;
          }
        });

        // Ensure string fields are properly handled (empty strings are fine, but undefined/null should be handled if API expects string)
        const stringFields = [
            'name', 'sh_venue_name', 'pw_venue_name', 'sh_parking_venue_name',
            'pm_venue_name', 'vivid_venue_name', 'timezone',
            'jp_venue_identifier', 'jp_venue_name', 'jp_url_slug'
        ];
        
        stringFields.forEach(field => {
            if (submitData[field] === undefined || submitData[field] === null) {
                // Keep null if that's what we want, or make empty string
                // Based on API endpoint code provided:
                // If it's undefined in req.body, it won't be updated in PUT. 
                // In POST, it inserts.
                // It seems fine to pass null or empty string.
                // However, let's ensure we are sending values the API expects.
            }
        });
        
        const url = this.isEditing 
          ? `https://api.spotjockeys.com/api/venue-mgt/${this.formData.id}`
          : 'https://api.spotjockeys.com/api/venue-mgt';
        
        const method = this.isEditing ? 'PUT' : 'POST';
        
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submitData)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        this.successMessage = result.message || `Venue ${this.isEditing ? 'updated' : 'created'} successfully`;
        this.closeModal();
        this.fetchVenues();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
        
      } catch (error) {
        console.error('Error submitting form:', error);
        this.errorMessage = 'Error submitting form: ' + error.message;
      } finally {
        this.isSubmitting = false;
      }
    },
    
    resetForm() {
      this.formData = {
        name: '',
        sh_venue_id: null,
        sh_venue_name: '',
        pw_venue_id: null,
        pw_venue_name: '',
        sh_parking_venue_id: null,
        sh_parking_venue_name: '',
        pm_venue_name: '',
        pm_venue_id: null,
        vivid_venue_id: null,
        vivid_venue_name: '',
        is_mlb: false,
        spothero_venue_id: null,
        sg_venue_id: null,
        timezone: '',
        pw_latitude: null,
        pw_longitude: null,
        mapping_certified: false,
        jp_venue_identifier: null,
        jp_venue_name: '',
        jp_url_slug: ''
      };
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errorMessage = '';
    },
    
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.venueToDelete = null;
    },
    
    // Pagination methods
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  }
};
</script>

<style scoped>
.venue-management {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
}

.header-section h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
}

.btn-info:hover:not(:disabled) {
  background-color: #138496;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.filters-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
}

.success-message {
  background: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
}

.table-section {
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.table-responsive {
  overflow-x: auto;
}

.venues-table {
  width: 100%;
  border-collapse: collapse;
}

.venues-table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.venues-table td {
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.venues-table tr:hover {
  background: #f8f9fa;
}

.status-yes {
  color: #28a745;
  font-weight: 600;
}

.status-no {
  color: #6c757d;
}

.actions-cell {
  white-space: nowrap;
  text-align: right;
}

.action-btn {
  width: 42px;
  height: 42px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition: transform 0.2s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.action-btn:active {
  transform: scale(0.95);
}

.action-btn i {
  font-size: 16px;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-info {
  font-weight: 600;
  color: #495057;
}

/* Modal custom styles removed in favor of Bootstrap Vue */
/* More modal styles removed */

.form-section h4 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.required::after {
  content: ' *';
  color: #dc3545;
}

.checkbox-label {
  flex-direction: row !important;
  align-items: center;
  gap: 8px;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

/* Venue Details Styles */
.venue-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.details-section {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
}

.details-section h4 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f8f9fa;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  font-weight: 600;
  color: #495057;
  flex: 0 0 40%;
}

.detail-row .value {
  flex: 1;
  text-align: right;
  color: #6c757d;
}

.warning-text {
  color: #dc3545;
  font-style: italic;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.fa-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .filter-row {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: 10px;
  }
  
  .venues-table {
    font-size: 12px;
  }
  
  .venues-table th,
  .venues-table td {
    padding: 8px 4px;
  }
  
  .modal-content {
    width: 95vw;
    margin: 20px;
  }
}

@media (max-width: 480px) {
  .venue-management {
    padding: 10px;
  }
}

/* Modern/iPad Form Styles */
.form-control-lg {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
}
.bg-soft-primary { background-color: #dfebfd !important; }
.bg-soft-success { background-color: #d1e7dd !important; }
.cursor-pointer { cursor: pointer; }
.form-group-modern label {
    margin-bottom: 0.5rem;
    letter-spacing: 0.5px;
}
.border-light { border-color: #e3e6f0 !important; }
.bg-light { background-color: #f8f9fc !important; }
.transition-all { transition: all 0.2s ease; }
</style>